/**
 * Choosing what a share lends, as a state machine with no DOM — the same shape as `share-flow.ts`,
 * so `test/app-lending-flow.test.ts` can walk every path.
 *
 * <p><b>The default is that a share lends nothing</b>, and that is not a formality. An ordinary share
 * hands over the app; the person who opens it connects their own Symphony and sees their own data, so
 * nothing they do can reach the owner. Lending is the other thing entirely: some of the app's calls
 * then run on the owner's account, in their absence. It is opt-in, it is per tool, and the dialog says
 * so in those terms rather than offering a switch labelled "share my connections".
 */

/** One tool the owner could lend, as offered by `/api/apps/lendable`. */
export interface LendableTool {
  name: string;
  description?: string;
}

export interface LendableConnection {
  id: string;
  name: string;
  usable: boolean;
  tools: LendableTool[];
}

export type LendableState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; connections: LendableConnection[] }
  | { status: 'error' };

/** A chosen tool, in the shape the share call takes. */
export interface ChosenTool {
  connectionId: string;
  toolName: string;
}

/**
 * Stable key for one choice, so selection is a set rather than a nested search.
 *
 * <p>JSON rather than a joined string, because a tool name may contain the delimiter. MCP does not
 * forbid a space in a tool name, and `"conn-1 send email now"` split on the first space yields the
 * tool `send` — so the owner ticked one tool, the summary counted it, and a DIFFERENT tool was
 * persisted, disclosed and enforced. Both phones model this as a struct and never had the bug.
 */
export function toolKey(connectionId: string, toolName: string): string {
  return JSON.stringify([connectionId, toolName]);
}

export function chosenFromKeys(keys: Iterable<string>): ChosenTool[] {
  return [...keys].map((key) => {
    const [connectionId, toolName] = JSON.parse(key) as [string, string];
    return { connectionId, toolName };
  });
}

/**
 * What the dialog may do next.
 *
 * <p>`canShare` is false while lending is switched on with nothing chosen, and that is deliberate
 * rather than a nicety: the server refuses that combination, so a button that fires it would report a
 * failure for a mistake the screen could already see. Saying "choose at least one" is the same
 * information, before the round trip.
 */
export interface LendingDecision {
  canShare: boolean;
  /** Why not, when not. The dialog resolves this key; it never writes copy of its own. */
  blockedKey?: 'apps.share.lend.chooseAtLeastOne';
}

export function lendingDecision(args: { lending: boolean; chosen: ReadonlySet<string>; emailValid: boolean }): LendingDecision {
  if (!args.emailValid) {
    return { canShare: false };
  }
  if (args.lending && args.chosen.size === 0) {
    return { canShare: false, blockedKey: 'apps.share.lend.chooseAtLeastOne' };
  }
  return { canShare: true };
}

/**
 * The sentence describing what is about to be handed over — the one a person reads before they agree.
 *
 * <p>It names a COUNT of tools and the connections by name, never "access to your connections". The
 * difference matters: the second sounds like a category and the first is the thing that is true. A
 * person lending two tools on one connection should not read a sentence that would equally describe
 * lending forty.
 */
export type LendSummary =
  | { kind: 'nothing'; key: 'apps.share.lend.nothing' }
  | { kind: 'some'; key: 'apps.share.lend.summary'; params: { count: number; connections: string } };

export function lendSummary(connections: LendableConnection[], chosen: ReadonlySet<string>): LendSummary {
  if (chosen.size === 0) {
    return { kind: 'nothing', key: 'apps.share.lend.nothing' };
  }
  const chosenTools = chosenFromKeys(chosen);
  const names = connections
    .filter((connection) => chosenTools.some((tool) => tool.connectionId === connection.id))
    .map((connection) => connection.name);
  return {
    kind: 'some',
    key: 'apps.share.lend.summary',
    params: { count: chosen.size, connections: names.join(', ') },
  };
}

/**
 * Toggling one tool. Returns a NEW set, so React state updates rather than mutating in place.
 */
export function toggleTool(chosen: ReadonlySet<string>, connectionId: string, toolName: string): Set<string> {
  const next = new Set(chosen);
  const key = toolKey(connectionId, toolName);
  if (next.has(key)) {
    next.delete(key);
  } else {
    next.add(key);
  }
  return next;
}

/**
 * Turning lending off clears the choices rather than remembering them.
 *
 * <p>A remembered list is a share that lends something the person stopped looking at. The one screen
 * where a forgotten selection hands somebody else the keys is not the place to be clever about
 * preserving input.
 */
export function clearChoices(): Set<string> {
  return new Set();
}


// ---------------------------------------------------------------------------------------------
// Reading the list: families, labels, and bulk ticks.
//
// Everything below is PRESENTATION ONLY. It changes how the tools are arranged and how many taps it
// takes to tick them; it never changes what a tick means or what gets sent. `chosenFromKeys` is still
// the one thing that decides what is lent, so a family that groups two tools oddly costs tidiness and
// nothing else. That is the property to preserve if this heuristic is ever tuned.
// ---------------------------------------------------------------------------------------------

/**
 * Verbs, and the connective tissue between them. These are what a tool name says it DOES, and grouping
 * by them produces families like "get" and "set" that scatter every real subject across all of them —
 * which is the arrangement this function exists to avoid.
 */
const ACTION_WORDS = new Set([
  'get', 'set', 'list', 'create', 'update', 'delete', 'remove', 'add', 'manage', 'run', 'call',
  'search', 'find', 'query', 'turn', 'send', 'read', 'write', 'open', 'close', 'start', 'stop',
  'toggle', 'bulk', 'is', 'has', 'or', 'and', 'the', 'a', 'by', 'for', 'to', 'of', 'all', 'info',
]);

/** Crude singularisation, so `areas` and `area` land in one family instead of two. */
function singular(word: string): string {
  if (word.length > 3 && word.endsWith('ies')) return `${word.slice(0, -3)}y`;
  if (word.length > 2 && word.endsWith('s') && !word.endsWith('ss')) return word.slice(0, -1);
  return word;
}

function segmentsOf(toolName: string): string[] {
  return toolName
    .split(/[_\-.\s]+/)
    .map((segment) => singular(segment.trim().toLowerCase()))
    .filter(Boolean);
}

/**
 * The leading segment every tool on a connection shares, if there is one — `ha` across `ha_get_addon`,
 * `ha_set_area_or_floor`, and so on. It is the connection's own name repeated on every row, so it
 * carries no information about which tool is which and is dropped before grouping.
 */
function sharedPrefix(tools: LendableTool[]): string | null {
  if (tools.length < 2) return null;
  const firsts = tools.map((tool) => segmentsOf(tool.name)[0]);
  const first = firsts[0];
  return first && firsts.every((segment) => segment === first) ? first : null;
}

export interface ToolFamily {
  /** Stable key for React and for the bulk-tick helpers. */
  key: string;
  /** What the family is called on screen. `null` means the catch-all, which the caller titles. */
  label: string | null;
  tools: LendableTool[];
}

/**
 * Arrange one connection's tools into families by SUBJECT, not by verb.
 *
 * <p>Each tool votes with the words in its name that are not verbs; the word most tools share becomes
 * the family. `ha_get_addon` and `ha_manage_addon` both say "addon" and land together, where grouping
 * on the first word would have filed them under "get" and "manage" — two families of one, and the
 * subject nowhere.
 *
 * <p>A tool whose name is all verbs has no subject to group on and goes to the catch-all (`label:
 * null`). So does the entire list when nothing is shared: one family per tool is the flat list again
 * with extra headings.
 */
export function groupIntoFamilies(tools: LendableTool[]): ToolFamily[] {
  if (tools.length === 0) return [];
  const prefix = sharedPrefix(tools);

  const meaningfulWords = (tool: LendableTool): string[] => {
    const segments = segmentsOf(tool.name);
    const withoutPrefix = prefix && segments[0] === prefix ? segments.slice(1) : segments;
    return withoutPrefix.filter((segment) => !ACTION_WORDS.has(segment));
  };

  // How many tools mention each word. The winner is the word that actually binds a group together —
  // a word only one tool uses would make a family of one, which is what the flat list already was.
  const frequency = new Map<string, number>();
  for (const tool of tools) {
    for (const word of new Set(meaningfulWords(tool))) {
      frequency.set(word, (frequency.get(word) ?? 0) + 1);
    }
  }

  const byFamily = new Map<string, LendableTool[]>();
  const orphans: LendableTool[] = [];
  for (const tool of tools) {
    // Ties break ALPHABETICALLY, not by the order the words happen to appear in this tool's name.
    // `list_floors_areas` and `remove_area_or_floor` mention the same two words as often as each
    // other; without a tie-break each follows its own word order and the two land in different
    // families — the exact split this grouping exists to prevent.
    const best = meaningfulWords(tool)
      .filter((word) => (frequency.get(word) ?? 0) > 1)
      .sort((a, b) => (frequency.get(b) ?? 0) - (frequency.get(a) ?? 0) || a.localeCompare(b))[0];
    if (!best) {
      orphans.push(tool);
      continue;
    }
    byFamily.set(best, [...(byFamily.get(best) ?? []), tool]);
  }

  const families: ToolFamily[] = [...byFamily.entries()]
    .sort((a, b) => b[1].length - a[1].length || a[0].localeCompare(b[0]))
    .map(([key, familyTools]) => ({ key, label: key, tools: familyTools }));
  if (orphans.length) {
    families.push({ key: '__other__', label: null, tools: orphans });
  }
  return families;
}

/**
 * A connection's name, shortened for reading — never renamed.
 *
 * <p>A self-hosted connection is addressed by a hostname whose first label is a machine-generated id
 * (`zms0nmrn5qrqr1nrdj1xpnrxsxgfrvav.ui.nabu.casa`). Thirty-two random characters are the widest thing
 * on the row and the least informative, so the heading drops that one label and keeps the rest. It is
 * a truncation, not a friendlier name invented for the service: anything that is not clearly a
 * generated id is returned untouched, and the caller still carries the full string as the row's title.
 */
export function connectionLabel(name: string): string {
  const labels = name.split('.');
  if (labels.length < 3) return name;
  const [first, ...rest] = labels;
  const looksGenerated = first.length >= 16 && /^[a-z0-9]+$/.test(first) && /[0-9]/.test(first);
  return looksGenerated ? rest.join('.') : name;
}

/** Whether every / some / none of a set of tools is currently ticked. Drives the bulk control's label. */
export type BulkState = 'none' | 'some' | 'all';

export function bulkState(chosen: ReadonlySet<string>, connectionId: string, tools: LendableTool[]): BulkState {
  if (tools.length === 0) return 'none';
  const ticked = tools.filter((tool) => chosen.has(toolKey(connectionId, tool.name))).length;
  if (ticked === 0) return 'none';
  return ticked === tools.length ? 'all' : 'some';
}

/**
 * Tick or untick a whole group at once. Returns a NEW set, and touches only the tools passed in — a
 * bulk control on one family must never disturb a neighbour's ticks.
 */
export function setBulk(
  chosen: ReadonlySet<string>,
  connectionId: string,
  tools: LendableTool[],
  selected: boolean,
): Set<string> {
  const next = new Set(chosen);
  for (const tool of tools) {
    const key = toolKey(connectionId, tool.name);
    if (selected) next.add(key);
    else next.delete(key);
  }
  return next;
}

// ---------------------------------------------------------------------------------------------
// Narrowing the list to what the app actually asked for.
//
// An app can DECLARE the tool names it was built to call (`MiniApp.required_tools`). When it has,
// the picker leads with those and pre-ticks them, so sharing a lamp switch does not ask anybody to
// read seventy-eight Home Assistant actions.
//
// Two rules keep a bad declaration cheap, and both matter more than the narrowing itself:
//
//   1. EMPTY MEANS UNKNOWN, never "needs nothing". Every app built before the field existed declares
//      none. Treating that as a requirement of zero tools would empty the picker for every app that
//      exists today, which is lending silently disappearing from the product.
//   2. A declaration that matches NOTHING the owner has is treated the same way. It happens whenever
//      the model named a tool wrongly, or the app targets a service this owner has not connected —
//      and an empty picker would leave them unable to lend anything at all, with no way to tell why.
//
// The declaration is a model's self-report, so it is a HINT for ordering and pre-ticking, never a
// gate: `showAll` reveals the whole list, and what is lent is still whatever is ticked.
// ---------------------------------------------------------------------------------------------

/** Case-insensitive, because a declaration is typed by a model and a tool list comes from a server. */
function requiredSet(requiredTools: readonly string[] | undefined): Set<string> {
  return new Set((requiredTools ?? []).map((tool) => tool.trim().toLowerCase()).filter(Boolean));
}

export interface NarrowedLendable {
  connections: LendableConnection[];
  /** True when the list is narrowed, i.e. the caller may offer "show everything". */
  narrowed: boolean;
  /** How many tools the full list holds, for the control that reveals them. */
  totalTools: number;
}

/**
 * The connections to render, given what the app declared and whether the owner asked to see it all.
 *
 * <p>Returns the FULL list unchanged whenever narrowing would not help — nothing declared, the owner
 * pressed "show all", or the declaration matches none of their tools. A connection left with no
 * matching tools drops out of the narrowed view rather than rendering as an empty heading.
 */
export function narrowToRequired(
  connections: LendableConnection[],
  requiredTools: readonly string[] | undefined,
  showAll: boolean,
): NarrowedLendable {
  const totalTools = connections.reduce((sum, connection) => sum + connection.tools.length, 0);
  const required = requiredSet(requiredTools);
  if (showAll || required.size === 0) {
    return { connections, narrowed: false, totalTools };
  }
  const narrowed = connections
    .map((connection) => ({ ...connection, tools: connection.tools.filter((tool) => required.has(tool.name.toLowerCase())) }))
    .filter((connection) => connection.tools.length > 0);
  // Matching nothing is the same answer as declaring nothing — see rule 2 above.
  if (narrowed.length === 0) {
    return { connections, narrowed: false, totalTools };
  }
  return { connections: narrowed, narrowed: true, totalTools };
}

/**
 * The tools to tick the moment lending is switched on.
 *
 * <p>Only ever the app's OWN declared tools, and only the ones this owner actually has. Pre-ticking
 * anything else would turn a per-tool grant into a bulk one on the single screen where that matters
 * most — which is why an app that declared nothing starts with NOTHING ticked, exactly as before.
 */
export function preselectRequired(connections: LendableConnection[], requiredTools: readonly string[] | undefined): Set<string> {
  const required = requiredSet(requiredTools);
  if (required.size === 0) return new Set();
  const chosen = new Set<string>();
  for (const connection of connections) {
    for (const tool of connection.tools) {
      if (required.has(tool.name.toLowerCase())) chosen.add(toolKey(connection.id, tool.name));
    }
  }
  return chosen;
}
